<template>
  <v-menu location="bottom start">
    <!-- Cheio quando a cor foi escolhida, vazado quando é automática: a diferença se lê
         sem abrir o menu, e é ela que diz se reordenar vai mudar esta cor. -->
    <template #activator="{props}">
      <!-- O ativador padrão é um botão, que é o que a tabela do fluxo quer: área de clique
           confortável numa célula. Quem precisa de outro desenho passa o seu — é o caso do
           editor de opções, que precisa da bolinha EXATAMENTE como o campo de valor padrão a
           desenha, e o botão em volta a encolhia e comia o respiro até o texto. -->
      <slot name="activator" v-bind="{props}">
      <v-btn v-bind="props" variant="text" icon size="small" :title="title">
        <!-- O VALUE_DOT em `small`, que é exatamente como o ChoiceInput desenha a opção
             escolhida: a mesma opção não pode ter duas caras na mesma tela — a do editor e a do
             campo de valor padrão logo abaixo dele. -->
        <v-icon size="small" :color="modelValue || fallback">
          {{ modelValue ? VALUE_DOT : 'mdi-circle-outline' }}
        </v-icon>
      </v-btn>
      </slot>
    </template>
    <v-sheet class="pa-2 color-swatch-menu">
      <div class="d-flex flex-wrap ga-1">
        <!-- A volta para a cor automática. Primeira da grade, e não última: é o padrão, e
             é de onde se sai. Emite cor vazia, que é como "não escolhida" se representa. -->
        <v-btn v-if="clearable" variant="text" icon size="small"
               :title="$tt('color-automatic')"
               @click="$emit('update:modelValue', '')">
          <v-icon :color="fallback">
            {{ modelValue ? 'mdi-circle-outline' : 'mdi-check-circle-outline' }}
          </v-icon>
        </v-btn>
        <!-- Botão por cor, e não um v-color-picker: as cores são as do Vuetify, um
             conjunto fechado, e é justamente isso que permite derivar os tons depois.
             Um seletor livre devolveria hexadecimal, e aí `teal-lighten-5` deixaria de
             existir. -->
        <v-btn v-for="color of palette" :key="color"
               variant="text" icon size="small"
               :title="color"
               @click="$emit('update:modelValue', color)">
          <v-icon :color="color">
            {{ color === modelValue ? 'mdi-check-circle' : usedBy(color) ? 'mdi-circle-slice-8' : 'mdi-circle' }}
          </v-icon>
        </v-btn>
      </div>
    </v-sheet>
  </v-menu>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import {PALETTE} from '@/utils/colors'
import {VALUE_DOT} from '@/types/ColoredOption'

/**
 * Escolha de cor entre as do Vuetify.
 *
 * É a única peça deste trabalho sem precedente no app: até aqui nenhuma cor era
 * escolhida, todas eram derivadas da ordem pelo StateColorizer. Fica em `shared` porque
 * a restrição que ela impõe — só cor-base da paleta — é o que mantém `shade` funcionando
 * em qualquer lugar que venha a usar cor escolhida.
 *
 * `used` não impede nada: marca as cores já ocupadas para a escolha ser informada. Quem
 * decide o que fazer ao repetir é quem usa o componente — no domínio, as duas trocam.
 *
 * Com `clearable`, a cor pode não ser escolhida: é o caso da situação de um fluxo, onde a
 * ausência significa "derive da ordem". O componente não sabe derivar nada — recebe em
 * `fallback` a cor em vigor, só para desenhar.
 */
@Component({name: 'ontos-color-swatch-menu', emits: ['update:modelValue']})
class ColorSwatchMenu extends Vue {
  //Exposta ao template: constante de módulo não é enxergada de lá
  VALUE_DOT = VALUE_DOT

  /** A cor escolhida, ou vazio quando não há escolha. */
  @Prop({type: String, default: ''}) readonly modelValue!: string
  @Prop({type: Array, default: () => []}) readonly used!: string[]
  @Prop({type: String, default: ''}) readonly title!: string
  /** Oferece a volta para "automática", emitindo cor vazia. */
  @Prop({type: Boolean, default: false}) readonly clearable!: boolean
  /** A cor em vigor quando não há escolha — só para o desenho. */
  @Prop({type: String, default: ''}) readonly fallback!: string

  get palette(): string[] {
    return PALETTE
  }

  usedBy(color: string): boolean {
    return this.used.includes(color)
  }
}

export default toNative(ColorSwatchMenu)
</script>

<style scoped>
/* Cinco por linha: com as 19 cores da paleta em quatro fileiras, o menu cabe sem rolar
   e as famílias vizinhas (vermelho, rosa, roxo) ficam perto umas das outras. */
.color-swatch-menu {
  max-width: 200px;
}
</style>
